"use client";

import { c as _c } from "react/compiler-runtime";
import { FileText, Plus, Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/submit-button";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
export interface DrawerPage {
  /** Entry row id — the "Overview" page is the entry itself. */
  id: string;
  title: string;
  isPublic: boolean;
  isOverview: boolean;
}
interface EntryPagesProps {
  pages: DrawerPage[];
  currentPageId: string;
  showAddPage: boolean;
  newPageTitle: string;
  creatingPage: boolean;
  onSelectPage: (pageId: string) => void;
  onShowAddPage: (show: boolean) => void;
  onNewPageTitleChange: (title: string) => void;
  onCreatePage: () => void;
  onDeletePage: (pageId: string) => Promise<void>;
}

/**
 * The Pages switcher — each Page is another document about the SAME entry
 * (e.g. "Overview", "Backstory", "Timeline"), independently public or private.
 * The drawer's rich-text area edits whichever page is selected here.
 */
export function EntryPages(t0) {
  const $ = _c(41);
  const {
    pages,
    currentPageId,
    showAddPage,
    newPageTitle,
    creatingPage,
    onSelectPage,
    onShowAddPage,
    onNewPageTitleChange,
    onCreatePage,
    onDeletePage
  } = t0;
  let t1;
  if ($[0] !== currentPageId || $[1] !== pages) {
    let t2;
    if ($[3] !== currentPageId) {
      t2 = p => p.id === currentPageId;
      $[3] = currentPageId;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = pages.find(t2);
    $[0] = currentPageId;
    $[1] = pages;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const current = t1;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FileText className="h-3 w-3" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== pages.length) {
    t3 = <Label className="text-xs flex items-center gap-1">{t2}Pages<span className="text-muted-foreground ml-1">({pages.length})</span></Label>;
    $[6] = pages.length;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onShowAddPage) {
    t4 = () => onShowAddPage(true);
    $[8] = onShowAddPage;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Plus className="h-3 w-3" aria-hidden={true} />;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4) {
    t6 = <button onClick={t4} data-testid="add-page-button" className="flex min-h-9 items-center gap-1 rounded-full bg-primary/15 px-3 py-0.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/25 sm:min-h-0 sm:px-2.5">{t5}New page</button>;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t6) {
    t7 = <div className="flex items-center justify-between">{t3}{t6}</div>;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== currentPageId || $[17] !== onDeletePage || $[18] !== onSelectPage || $[19] !== pages) {
    let t9;
    if ($[21] !== currentPageId || $[22] !== onDeletePage || $[23] !== onSelectPage) {
      t9 = page => <div key={page.id} className={`flex w-full items-center rounded-lg border transition-colors ${page.id === currentPageId ? "border-primary/50 bg-primary/10" : "border-border/40 bg-muted/40 hover:border-primary/40"}`}><button onClick={() => onSelectPage(page.id)} data-testid={`page-row-${page.title}`} className="flex min-h-11 min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5 text-left sm:min-h-0"><FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden={true} /><span className="flex-1 truncate text-xs font-medium text-foreground">{page.title}</span><span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${page.isPublic ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-secondary text-muted-foreground"}`}>{page.isPublic ? "Public" : "Private"}</span></button>{!page.isOverview && <ConfirmDeleteDialog title="Delete page" description={`Delete "${page.title}"? This cannot be undone.`} onConfirm={() => onDeletePage(page.id)} trigger={<button type="button" aria-label={`Delete ${page.title} page`} data-testid={`delete-page-${page.title}`} className="mr-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive sm:h-7 sm:w-7"><Trash2 className="h-3.5 w-3.5" aria-hidden={true} /></button>} />}</div>;
      $[21] = currentPageId;
      $[22] = onDeletePage;
      $[23] = onSelectPage;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t8 = pages.map(t9);
    $[16] = currentPageId;
    $[17] = onDeletePage;
    $[18] = onSelectPage;
    $[19] = pages;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[25] !== t8) {
    t9 = <div className="space-y-1">{t8}</div>;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== creatingPage || $[28] !== newPageTitle || $[29] !== onCreatePage || $[30] !== onNewPageTitleChange || $[31] !== onShowAddPage || $[32] !== showAddPage) {
    t10 = showAddPage && <div className="flex items-center gap-1.5 pt-0.5"><Input value={newPageTitle} onChange={e => onNewPageTitleChange(e.target.value)} placeholder={"Page title \u2014 e.g. Backstory, Timeline\u2026"} className="h-7 flex-1 text-xs" autoFocus={true} data-testid="new-page-title-input" onKeyDown={e_0 => {
        if (e_0.key === "Enter") {
          onCreatePage();
        }
        if (e_0.key === "Escape") {
          onShowAddPage(false);
        }
      }} /><SubmitButton size="sm" className="h-7 px-2 text-xs" onClick={onCreatePage} disabled={!newPageTitle.trim()} loading={creatingPage} loadingLabel={"\u2026"} label="Add" data-testid="create-page-button" /></div>;
    $[27] = creatingPage;
    $[28] = newPageTitle;
    $[29] = onCreatePage;
    $[30] = onNewPageTitleChange;
    $[31] = onShowAddPage;
    $[32] = showAddPage;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== current) {
    t11 = current && !current.isOverview && <p className="text-[10px] italic text-muted-foreground">Viewing <span className="not-italic font-medium text-foreground">{current.title}</span> — the content below is this page</p>;
    $[34] = current;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t7 || $[39] !== t9) {
    t12 = <div className="px-4 py-2 space-y-1.5" data-testid="entry-pages">{t7}{t9}{t10}{t11}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t7;
    $[39] = t9;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
}